import { useEffect, useMemo, useState } from 'react'
import { AUTH_STORAGE_KEY } from '@/services/http'
import authApi from './auth.api'
import { IAuthSession } from './auth.types'

const readStoredSession = (): IAuthSession | null => {
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY) || window.sessionStorage.getItem(AUTH_STORAGE_KEY)
    if (!raw) return null

    const session = JSON.parse(raw) as IAuthSession
    if (!session?.accessToken || !session?.expires || Date.now() > session.expires) return null

    return session
  } catch {
    window.localStorage.removeItem(AUTH_STORAGE_KEY)
    return null
  }
}

const useAuthSession = () => {
  const [session, setSessionState] = useState<IAuthSession | null>(() => readStoredSession())
  const [checkingSession, setCheckingSession] = useState(Boolean(session))

  useEffect(() => {
    if (!session) {
      setCheckingSession(false)
      return
    }

    authApi.getSelf().then((response) => {
      if (response.status === 'success') {
        setSessionState((prev) => (prev ? { ...prev, user: response.body } : prev))
      } else {
        window.localStorage.removeItem(AUTH_STORAGE_KEY)
        window.sessionStorage.removeItem(AUTH_STORAGE_KEY)
        setSessionState(null)
      }

      setCheckingSession(false)
    })
  }, [])

  const setSession = (nextSession: IAuthSession, persist = true) => {
    const storage = persist ? window.localStorage : window.sessionStorage
    window.localStorage.removeItem(AUTH_STORAGE_KEY)
    window.sessionStorage.removeItem(AUTH_STORAGE_KEY)
    storage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextSession))
    setSessionState(nextSession)
  }

  const logout = () => {
    window.localStorage.removeItem(AUTH_STORAGE_KEY)
    window.sessionStorage.removeItem(AUTH_STORAGE_KEY)
    setSessionState(null)
  }

  return useMemo(
    () => ({ session, checkingSession, setSession, logout }),
    [session, checkingSession]
  )
}

export type AuthSessionCtrl = ReturnType<typeof useAuthSession>
export default useAuthSession
